import { useEffect, useRef, useState } from 'react';
import { FileSearch, LoaderCircle, ZoomIn, ZoomOut } from 'lucide-react';
import type { PageResult } from '../shared/desktop-api';
import { sourcePreviewCache, watchSourcePreview } from './field-source-preview-cache';
import './suggestion-supporting-page.css';
import { TooltipButton } from './TooltipButton';

export type SuggestionSupportingDocument = {
  id: string;
  sourceName: string;
  documentType: string;
  pages: readonly Pick<PageResult, 'number' | 'previewPath'>[];
  pageCount?: number;
};

/** Original retained page only. A suggestion identifies supporting documents,
 * not a field/region, so this view deliberately invents no highlighted answer. */
export function SupportingPage({
  document,
  page,
}: {
  document: SuggestionSupportingDocument;
  page: SuggestionSupportingDocument['pages'][number];
}) {
  const identity = JSON.stringify([document.id, page.number, page.previewPath]);
  const [loaded, setLoaded] = useState<{ identity: string; source?: string; failed?: boolean }>();
  const [zoom, setZoom] = useState(100);
  const viewport = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setLoaded(undefined);
    setZoom(100);
    if (viewport.current) viewport.current.scrollTo(0, 0);
    if (!page.previewPath) {
      setLoaded({ identity, failed: true });
      return;
    }
    // The existing renderer-wide cache bounds rapid navigation to two reads,
    // validates passive raster bytes/dimensions, and cancels retired listeners.
    return watchSourcePreview(
      sourcePreviewCache,
      `suggestion:${identity}`,
      page.previewPath,
      (preview) => setLoaded({ identity, source: preview.source }),
      () => setLoaded({ identity, failed: true }),
    );
  }, [identity]);
  const current = loaded?.identity === identity ? loaded : undefined;
  return (
    <div className="improvements-paper">
      <div className="improvements-image-tools" role="group" aria-label="Supporting page view">
        <TooltipButton
          type="button"
          onClick={() => setZoom((value) => Math.max(100, value - 25))}
          disabled={zoom === 100}
          aria-label="Zoom out supporting page"
          tooltip="Zoom out"
        >
          <ZoomOut size={16} />
        </TooltipButton>
        <button type="button" onClick={() => setZoom(100)} aria-label="Fit supporting page width">
          Fit width
        </button>
        <output aria-label="Supporting page zoom">{zoom}%</output>
        <TooltipButton
          type="button"
          onClick={() => setZoom((value) => Math.min(250, value + 25))}
          disabled={zoom === 250}
          aria-label="Zoom in supporting page"
          tooltip="Zoom in"
        >
          <ZoomIn size={16} />
        </TooltipButton>
      </div>
      <div
        ref={viewport}
        className="improvements-paper-scroll"
        tabIndex={0}
        aria-label={`Supporting document ${document.sourceName}, page ${page.number}`}
      >
        {current?.source ? (
          <img
            src={current.source}
            alt={`${document.sourceName} · page ${page.number}`}
            draggable={false}
            style={{ width: `${zoom}%` }}
            onError={() => setLoaded({ identity, failed: true })}
          />
        ) : (
          <div className="improvements-empty-page" role="status">
            {current?.failed ? (
              <>
                <FileSearch size={30} />
                <strong>Page preview unavailable</strong>
                <span>The suggestion and your choices have not changed.</span>
              </>
            ) : (
              <>
                <LoaderCircle size={24} className="spin" />
                <span>Loading supporting page…</span>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
